import type { Issue } from "./types";
import { humanOwnerId } from "./taskOwnership";

export type AttentionQueueMode = "inbox" | "awaiting";

export type AttentionSnoozeState = Readonly<
  Record<string, number | null | undefined>
>;

export interface AttentionQueuePartition {
  active: Issue[];
  snoozed: Issue[];
}

export interface AttentionSnoozeChoice {
  id: "later-today" | "tomorrow" | "next-week";
  label: string;
  until: number;
}

const CLOSED_STATUSES = new Set<Issue["status"]>(["done", "canceled"]);

function priorityRank(priority: Issue["priority"]): number {
  return priority === 0 ? 5 : priority;
}

export function isAwaitingAttention(
  issue: Issue,
  currentUserId: string | null
): boolean {
  return (
    currentUserId != null &&
    issue.requesterId === currentUserId &&
    issue.status === "in_review"
  );
}

export function isInboxAttention(
  issue: Issue,
  currentUserId: string | null
): boolean {
  if (
    currentUserId == null ||
    humanOwnerId(issue) !== currentUserId ||
    CLOSED_STATUSES.has(issue.status)
  ) {
    return false;
  }

  // A task can be both assigned to and reported by the same person. Keep the
  // two attention queues mutually exclusive: review work belongs in Awaiting,
  // while Inbox remains the person's execution queue.
  return !isAwaitingAttention(issue, currentUserId);
}

export function compareAttentionIssues(a: Issue, b: Issue, now = Date.now()): number {
  const aOverdue = a.dueDate != null && a.dueDate < now ? 0 : 1;
  const bOverdue = b.dueDate != null && b.dueDate < now ? 0 : 1;
  if (aOverdue !== bOverdue) return aOverdue - bOverdue;

  const aDue = a.dueDate ?? Number.POSITIVE_INFINITY;
  const bDue = b.dueDate ?? Number.POSITIVE_INFINITY;
  if (aDue !== bDue) return aDue - bDue;

  const priorityDelta = priorityRank(a.priority) - priorityRank(b.priority);
  if (priorityDelta !== 0) return priorityDelta;

  return b.updatedAt - a.updatedAt;
}

export function partitionAttentionQueue({
  issues,
  mode,
  currentUserId,
  snoozedUntilByIssueId,
  now = Date.now(),
}: {
  issues: Issue[];
  mode: AttentionQueueMode;
  currentUserId: string | null;
  snoozedUntilByIssueId: AttentionSnoozeState;
  now?: number;
}): AttentionQueuePartition {
  const matches =
    mode === "awaiting"
      ? (issue: Issue) => isAwaitingAttention(issue, currentUserId)
      : (issue: Issue) => isInboxAttention(issue, currentUserId);

  const active: Issue[] = [];
  const snoozed: Issue[] = [];

  for (const issue of issues) {
    if (!matches(issue)) continue;
    const snoozedUntil = snoozedUntilByIssueId[issue.id];
    if (snoozedUntil != null && snoozedUntil > now) snoozed.push(issue);
    else active.push(issue);
  }

  active.sort((a, b) => compareAttentionIssues(a, b, now));
  snoozed.sort((a, b) => {
    const snoozeDelta =
      (snoozedUntilByIssueId[a.id] ?? Number.POSITIVE_INFINITY) -
      (snoozedUntilByIssueId[b.id] ?? Number.POSITIVE_INFINITY);
    return snoozeDelta || compareAttentionIssues(a, b, now);
  });

  return { active, snoozed };
}

export function attentionSnoozeChoices(now = Date.now()): AttentionSnoozeChoice[] {
  const current = new Date(now);

  const tomorrow = new Date(current);
  tomorrow.setDate(current.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);

  const nextMonday = new Date(current);
  const daysUntilMonday = ((8 - current.getDay()) % 7) || 7;
  nextMonday.setDate(current.getDate() + daysUntilMonday);
  nextMonday.setHours(9, 0, 0, 0);

  return [
    {
      id: "later-today",
      label: "Later today",
      until: now + 4 * 60 * 60 * 1000,
    },
    {
      id: "tomorrow",
      label: "Tomorrow morning",
      until: tomorrow.getTime(),
    },
    {
      id: "next-week",
      label: "Next Monday",
      until: nextMonday.getTime(),
    },
  ];
}
